/**
 * Where an order stands, and what RM24 should do next (PB-14).
 *
 * The order page showed every tool at once — review, prepayment, two staffing
 * calendars, partner offers, completion — and up to five equal primary
 * buttons, so nothing said which one this order needed now. The server knows
 * exactly what holds an order back (review, payment gate, staffing); this is
 * that knowledge turned into one recommended step, without adding a rule.
 *
 * Read-only and pure: every fact comes from the Job documents of the order,
 * mirrored from the server's own gates (`prepayCleared`, `canConfirmReview`,
 * `coverageByDay`). The server still decides whether an action is allowed.
 *
 * Not a linear wizard. Trades of one order move separately — one can be
 * running while another still needs people — so steps are per trade and the
 * order's view is the honest sum: a step is done only when every trade it
 * applies to has done it, and the summary says when trades have diverged.
 */

import { calendarDate, jobTimeZone, prepayCleared } from '@rendimees24/shared-types';
import type { DayCoverage, Job } from '@rendimees24/shared-types';

import { coverageSummary } from './coverage';
import { canConfirmReview } from './reviewGate';

export type StepKey =
  | 'address'
  | 'terms'
  | 'payment'
  | 'staffing'
  | 'confirm'
  | 'ready'
  | 'running'
  | 'completed';

/** Canonical order. Steps that apply to no trade are left out, not greyed. */
export const STEP_ORDER: StepKey[] = [
  'address',
  'terms',
  'payment',
  'staffing',
  'confirm',
  'ready',
  'running',
  'completed',
];

export type StepState = 'done' | 'current' | 'partial' | 'todo';

export interface ProgressStep {
  key: StepKey;
  state: StepState;
  /** Trades this step applies to, and how many have done it. */
  applies: number;
  done: number;
}

/** What the recommendation asks of the admin — the kind, not the colour. */
export type NextKind = 'action' | 'warn' | 'wait' | 'none' | 'loading';

export type NextCode =
  | 'loading'
  | 'verifyAddress'
  | 'reviewTerms'
  | 'issueProforma'
  | 'paymentOverdue'
  | 'awaitPayment'
  | 'staff'
  | 'confirmOrder'
  | 'reviewReports'
  | 'reportsUnknown'
  | 'reviewCompletion'
  /** EPIC 56 / P13 — the order's time ran out without a known outcome. */
  | 'resolveLifecycle'
  | 'ready'
  | 'running'
  | 'completed'
  | 'cancelled';

/** Which tool on the page the step opens. */
export type NextTarget =
  | 'review'
  | 'payment'
  | 'staffing'
  | 'reports'
  | 'completion'
  | 'lifecycle'
  | null;

export interface TradeShortage {
  jobId: string;
  roleRequired: string;
  /** Places asked for at the same time. */
  required: number;
  /**
   * EPIC 56 — people on at the first short shift (`required − shortBy`), not
   * the number of names on the trade.
   */
  assigned: number;
  /** Working days (from today on) with fewer people than ordered. */
  shortDays: number;
  /** The first short shift: its local date and how many are missing. */
  firstShort?: { date: string; shortBy: number } | null;
}

export interface NextItem {
  code: NextCode;
  kind: NextKind;
  target: NextTarget;
  /** Trades the item is about. */
  jobIds: string[];
  shortages?: TradeShortage[];
  /** ISO instant the item refers to: payment deadline, start or end. */
  at?: string;
  count?: number;
}

/** One trade's place, for the "ordered work" overview. */
export interface TradeProgress {
  jobId: string;
  /** Staffing readiness — not the work itself. */
  staffing: 'staffed' | 'partner' | 'short' | 'blocked';
  shortDays: number;
  /** EPIC 56 — the first short shift, for "Neljapäeval puudub 1". */
  firstShort?: { date: string; shortBy: number } | null;
  /** The work period, by the order's own times — never the work's completion. */
  work: 'notStarted' | 'running' | 'ended' | 'completed' | 'cancelled';
}

export interface OrderProgress {
  steps: ProgressStep[];
  trades: TradeProgress[];
  next: NextItem;
  /** Other things that need attention — never hidden behind `next`. */
  attention: NextItem[];
  /**
   * Set when trades are in different places: the furthest one and how many
   * are still behind, so "work running" does not hide a trade without people.
   */
  diverged: { lead: StepKey; behind: StepKey; behindCount: number } | null;
  /** Non-cancelled trades; 0 when the whole order is cancelled. */
  liveTrades: number;
}

export interface ProgressOptions {
  /** Current instant (ISO). Injected so the logic is testable. */
  now: string;
  /** The order's trades are still loading: claim nothing. */
  loading?: boolean;
  /** Worker submissions / adjustments awaiting a decision, per trade. */
  pendingReports?: Record<string, number>;
  /**
   * Trades whose report state is not known (still loading, or the read
   * failed). Unknown is never read as zero: such a trade cannot be "nothing
   * to do".
   */
  reportsUnknown?: ReadonlySet<string>;
}

const COMMERCIAL = new Set(['non_contract', 'payment_risk']);
/** Reviews that end in "confirm the order to the client". */
const STAFFING_REVIEW = new Set(['unstaffed', 'partial', 'address_unverified']);

/** PB-15 — the client typed the address; RM24 has not confirmed it. */
export function addressUnverified(job: Pick<Job, 'requirements'>): boolean {
  return job.requirements?.location?.addressRef?.provider === 'unverified';
}

function isClosed(job: Pick<Job, 'status'>): boolean {
  return job.status === 'completed' || job.status === 'invoiced';
}

/**
 * Shifts from `fromDay` on where fewer people are on at once than ordered.
 * EPIC 56 — the shared staffing arithmetic (`jobCoverage`), the same one the
 * server judges with: a person counts only on the shifts of their own
 * segments, and two half-days back to back fill one place.
 */
export function shortWorkingDays(job: Job, fromDay: string): number {
  if (!job.requirements?.startAt || !job.requirements?.endAt) return 0;
  return coverageSummary(job, fromDay === '0000-01-01' ? undefined : fromDay).shortShifts;
}

/** The first short shift from `fromDay` on — "Neljapäeval puudub 1". */
export function firstShortShift(job: Job, fromDay: string): DayCoverage | null {
  if (!job.requirements?.startAt || !job.requirements?.endAt) return null;
  return coverageSummary(job, fromDay === '0000-01-01' ? undefined : fromDay).firstShort;
}

interface TradeView {
  job: Job;
  applies: Set<StepKey>;
  done: Set<StepKey>;
  /** The first step it applies to and has not done. */
  behind: StepKey;
  /** The furthest step it has reached. */
  reached: StepKey;
  items: NextItem[];
  trade: TradeProgress;
}

function viewTrade(job: Job, opts: ProgressOptions): TradeView {
  const now = opts.now;
  const zone = jobTimeZone(job);
  const today = calendarDate(now, zone);
  const review = job.review;
  const reviewPending = review?.status === 'pending';
  const commercial = !!review && COMMERCIAL.has(review.reason);
  const staffingReview = !!review && STAFFING_REVIEW.has(review.reason);
  const prepay = job.payment?.mode === 'prepay';
  const cleared = prepayCleared(job);
  const closed = isClosed(job);
  const startAt = job.requirements?.startAt ?? '';
  const endAt = job.requirements?.endAt ?? '';
  const started = !!startAt && now >= startAt;
  const ended = !!endAt && now >= endAt;
  const partner = !!job.sourcing?.awardedProviderId;
  const unverified = addressUnverified(job);
  // Days still ahead are what staffing can change; a running order is judged
  // on what remains, not on days already worked. An order whose period has
  // passed has no days ahead — it is judged on its whole period, or an order
  // that ran with nobody would read as staffed.
  const fromDay = ended ? '0000-01-01' : today;
  const short = closed ? 0 : shortWorkingDays(job, fromDay);
  const gap = closed || short === 0 ? null : firstShortShift(job, fromDay);
  const firstShort = gap ? { date: gap.date, shortBy: gap.shortBy } : null;
  const staffed = closed || partner || short === 0;

  const applies = new Set<StepKey>(['staffing', 'ready', 'running', 'completed']);
  if (unverified || review?.reason === 'address_unverified') applies.add('address');
  if (commercial) applies.add('terms');
  if (prepay) applies.add('payment');
  if (staffingReview) applies.add('confirm');

  const done = new Set<StepKey>();
  if (!unverified) done.add('address');
  if (commercial && !reviewPending) done.add('terms');
  if (prepay && cleared) done.add('payment');
  if (staffed) done.add('staffing');
  if (staffingReview && !reviewPending) done.add('confirm');
  // Ready means every step before it is done — a passed start time alone
  // only means the planned work period has begun, not that it was ready.
  const prerequisites = (
    ['address', 'terms', 'payment', 'staffing', 'confirm'] as StepKey[]
  ).filter((k) => applies.has(k));
  const prepared = prerequisites.every((k) => done.has(k));
  // Ticked once the order was ready AND its period began; until the start a
  // ready order sits at this step with nothing to do.
  if (closed || (prepared && started)) done.add('ready');
  // A completed or invoiced trade has been through every step, whatever an
  // older field still says.
  if (closed) for (const k of applies) done.add(k);

  const items: NextItem[] = [];
  const ids = [job.id];
  // Reports are decided after the work too, so a completed or invoiced trade
  // still surfaces one that waits.
  if (job.status !== 'cancelled') {
    const reports = opts.pendingReports?.[job.id] ?? 0;
    if (opts.reportsUnknown?.has(job.id)) {
      items.push({ code: 'reportsUnknown', kind: 'wait', target: 'reports', jobIds: ids });
    } else if (reports > 0) {
      items.push({
        code: 'reviewReports',
        kind: 'action',
        target: 'reports',
        jobIds: ids,
        count: reports,
      });
    }
  }
  if (job.status !== 'cancelled' && !closed) {
    // Asked first: the review cannot be confirmed, nor the proforma issued,
    // until RM24 has confirmed where the work is (server: canConfirmReview,
    // planOrderProforma).
    if (unverified) {
      items.push({ code: 'verifyAddress', kind: 'action', target: 'review', jobIds: ids });
    }
    if (reviewPending && commercial) {
      items.push({ code: 'reviewTerms', kind: 'action', target: 'review', jobIds: ids });
    }
    if (prepay && !cleared && !(reviewPending && commercial) && !unverified) {
      const deadline = job.paymentDeadlineAt;
      if (!job.prepaymentInvoiceId) {
        items.push({ code: 'issueProforma', kind: 'action', target: 'payment', jobIds: ids });
      } else if (job.paymentStatus === 'overdue' || (!!deadline && now >= deadline)) {
        items.push({
          code: 'paymentOverdue',
          kind: 'warn',
          target: 'payment',
          jobIds: ids,
          at: deadline,
        });
      } else {
        items.push({
          code: 'awaitPayment',
          kind: 'wait',
          target: 'payment',
          jobIds: ids,
          at: deadline,
        });
      }
    }
    // Staffing is offered only once it can be written: the server refuses to
    // assign a prepay order before it is cleared, and a commercial review
    // comes first. A period already over cannot be staffed any more.
    if (!staffed && !ended && cleared && !(reviewPending && commercial)) {
      items.push({
        code: 'staff',
        kind: 'action',
        target: 'staffing',
        jobIds: ids,
        shortages: [
          {
            jobId: job.id,
            roleRequired: job.requirements?.roleRequired ?? '',
            required: Math.max(1, job.requirements?.workerCount ?? 1),
            assigned: gap ? gap.covered : Math.max(1, job.requirements?.workerCount ?? 1),
            shortDays: short,
            firstShort,
          },
        ],
      });
    }
    if (reviewPending && staffingReview && canConfirmReview(job).allowed) {
      items.push({ code: 'confirmOrder', kind: 'action', target: 'review', jobIds: ids });
    }
    // EPIC 56 / P13 — a lapsed order is a case to resolve, not "running" and
    // not merely "review completion": the case card says what is unknown and
    // offers did-not-happen / new time / end of work.
    if (job.lifecycleIssue) {
      items.push({
        code: 'resolveLifecycle',
        kind: 'action',
        target: 'lifecycle',
        jobIds: ids,
        at: job.lifecycleIssue.firstDetectedAt,
      });
    } else if (ended && (job.status === 'assigned' || job.status === 'in_progress')) {
      items.push({
        code: 'reviewCompletion',
        kind: 'action',
        target: 'completion',
        jobIds: ids,
        at: endAt,
      });
    } else if (started) {
      items.push({ code: 'running', kind: 'none', target: null, jobIds: ids, at: endAt });
    } else if (prepared) {
      items.push({ code: 'ready', kind: 'none', target: null, jobIds: ids, at: startAt });
    }
  } else if (closed) {
    items.push({ code: 'completed', kind: 'none', target: null, jobIds: ids });
  }

  const relevant = STEP_ORDER.filter((s) => applies.has(s));
  const behind = relevant.find((s) => !done.has(s)) ?? 'completed';
  const reached: StepKey = closed ? 'completed' : started ? 'running' : behind;
  const trade: TradeProgress = {
    jobId: job.id,
    staffing:
      closed || short === 0
        ? 'staffed'
        : partner
          ? 'partner'
          : !cleared || (reviewPending && commercial)
            ? 'blocked'
            : 'short',
    shortDays: short,
    firstShort,
    work: closed ? 'completed' : ended ? 'ended' : started ? 'running' : 'notStarted',
  };
  return { job, applies, done, behind, reached, items, trade };
}

/** Lower is more urgent. Actions follow the order's own sequence. */
const ACTION_RANK: Record<NextCode, number> = {
  resolveLifecycle: -2,
  verifyAddress: -1,
  reviewTerms: 0,
  issueProforma: 1,
  staff: 2,
  confirmOrder: 3,
  reviewReports: 4,
  reportsUnknown: 15,
  reviewCompletion: 5,
  paymentOverdue: 10,
  awaitPayment: 20,
  running: 30,
  ready: 31,
  completed: 32,
  cancelled: 33,
  loading: 40,
};

const KIND_RANK: Record<NextKind, number> = { action: 0, warn: 1, wait: 2, none: 3, loading: 4 };

/** Same item on several trades becomes one item naming all of them. */
function merge(items: NextItem[]): NextItem[] {
  const byCode = new Map<NextCode, NextItem>();
  for (const it of items) {
    const prev = byCode.get(it.code);
    if (!prev) {
      byCode.set(it.code, {
        ...it,
        jobIds: [...it.jobIds],
        shortages: it.shortages && [...it.shortages],
      });
      continue;
    }
    prev.jobIds.push(...it.jobIds);
    if (it.shortages) prev.shortages = [...(prev.shortages ?? []), ...it.shortages];
    if (it.count) prev.count = (prev.count ?? 0) + it.count;
    // The earliest deadline or start is the one that matters; for "running"
    // the latest end is when the order as a whole is over.
    if (it.at && (!prev.at || (it.code === 'running' ? it.at > prev.at : it.at < prev.at))) {
      prev.at = it.at;
    }
  }
  return [...byCode.values()].sort(
    (a, b) => KIND_RANK[a.kind] - KIND_RANK[b.kind] || ACTION_RANK[a.code] - ACTION_RANK[b.code],
  );
}

export function deriveOrderProgress(jobs: Job[], opts: ProgressOptions): OrderProgress {
  const live = jobs.filter((j) => j.status !== 'cancelled');
  const views = live.map((j) => viewTrade(j, opts));
  const trades: TradeProgress[] = jobs.map(
    (j) =>
      views.find((v) => v.job.id === j.id)?.trade ?? {
        jobId: j.id,
        staffing: 'staffed',
        shortDays: 0,
        work: 'cancelled',
      },
  );

  const steps: ProgressStep[] = [];
  const behindIdx = views.length
    ? Math.min(...views.map((v) => STEP_ORDER.indexOf(v.behind)))
    : STEP_ORDER.length - 1;
  for (const key of STEP_ORDER) {
    const applying = views.filter((v) => v.applies.has(key));
    if (applying.length === 0) continue;
    const doneCount = applying.filter((v) => v.done.has(key)).length;
    const idx = STEP_ORDER.indexOf(key);
    const state: StepState =
      doneCount === applying.length
        ? 'done'
        : idx === behindIdx
          ? 'current'
          : doneCount > 0 ||
              // The work period has begun for some trade even though an
              // earlier step is still open — said as such, never as done.
              (key === 'running' && views.some((v) => v.reached === 'running'))
            ? 'partial'
            : 'todo';
    steps.push({ key, state, applies: applying.length, done: doneCount });
  }

  if (opts.loading) {
    return {
      steps,
      trades,
      next: { code: 'loading', kind: 'loading', target: null, jobIds: [] },
      attention: [],
      diverged: null,
      liveTrades: live.length,
    };
  }

  if (live.length === 0) {
    return {
      steps,
      trades,
      next: { code: 'cancelled', kind: 'none', target: null, jobIds: jobs.map((j) => j.id) },
      attention: [],
      diverged: null,
      liveTrades: 0,
    };
  }

  const ranked = merge(views.flatMap((v) => v.items));
  // While anything still needs doing, "nothing to do" is not the answer for
  // the order — only for some of its trades.
  const pressing = ranked.filter((i) => i.kind !== 'none');
  const next = pressing[0] ??
    ranked[0] ?? { code: 'ready', kind: 'none', target: null, jobIds: [] };
  // Waiting on another trade's payment is not a task, but hiding it would let
  // the order read as if staffing were all that is left.
  const attention = ranked.filter((i) => i !== next && i.kind !== 'none');

  const leadIdx = Math.max(...views.map((v) => STEP_ORDER.indexOf(v.reached)));
  const lagIdx = Math.min(...views.map((v) => STEP_ORDER.indexOf(v.behind)));
  const diverged =
    views.length > 1 && leadIdx > lagIdx
      ? {
          lead: STEP_ORDER[leadIdx],
          behind: STEP_ORDER[lagIdx],
          behindCount: views.filter((v) => STEP_ORDER.indexOf(v.behind) === lagIdx).length,
        }
      : null;

  return { steps, trades, next, attention, diverged, liveTrades: live.length };
}
